import BaseStepHandler from './BaseStepHandler';
import QRCodeModal from '../../ui/QRCodeModal';
import { Image } from 'fabric';

/**
 * Handler for QR code step type
 */
export default class QRCodeStepHandler extends BaseStepHandler {
    /**
     * Create a new QRCodeStepHandler
     */
    constructor(stepId, stepDefinition, canvas, stepManager, side) {
        super(stepId, stepDefinition, canvas, stepManager, side);
        this.qrCodeModal = null;
        this.qrCodeElement = null;
        this.qrCodeElementId = `${this.stepId}_qrcode`;
    }
    
    /**
     * Initialize QR code step
     */
    initialize() {
        super.initialize();
        
        // If we have element IDs in the definition, store references
        if (this.definition.elements && this.definition.elements.length > 0) {
            const elementId = this.definition.elements[0];
            const element = this.canvas.getElement(elementId);
            if (element) {
                this.elements.set(elementId, element);
                this.qrCodeElement = element;
            }
        }
        
        return this;
    }
    
    /**
     * Process template when no data exists
     */
    processTemplate() {
        // Get step data
        const stepData = this.getStepData();
        
        // If we have data, update with it (will handle visibility)
        if (stepData && Object.keys(stepData).length > 0) {
            this.update(stepData);
        } else {
            // Otherwise hide the QR code by default
            this.hide();
        }
        
        return this;
    }
    
    /**
     * Activate QR code step
     */
    activate() {
        super.activate();
        
        // Make QR code element interactive if it exists
        if (this.qrCodeElement) {
            this.qrCodeElement.set({
                selectable: true,
                hasControls: this.definition.allowedActions?.includes('scale') ?? true,
                lockMovementX: !(this.definition.allowedActions?.includes('move') ?? false),
                lockMovementY: !(this.definition.allowedActions?.includes('move') ?? false),
                cornerStyle: 'circle',
                transparentCorners: false,
                cornerColor: '#0d6efd',
                cornerSize: 50,
                borderScaleFactor:10,
                padding: 10
            });
            
            this.canvas.canvas.setActiveObject(this.qrCodeElement);
            this.canvas.canvas.requestRenderAll();
        }
        
        return this;
    }
    
    /**
     * Deactivate QR code step
     */
    deactivate() {
        super.deactivate();
        
        // Make QR code element non-interactive
        if (this.qrCodeElement) {
            this.qrCodeElement.set({
                selectable: false,
                hasControls: false,
                lockMovementX: true,
                lockMovementY: true
            });
            
            this.canvas.canvas.requestRenderAll();
        }
        
        return this;
    }
    
    /**
     * Update QR code element with step data
     * 
     * @param {object} stepData - Step data
     */
    update(stepData) {
        super.update(stepData);
        
        // If not enabled, hide the QR code element
        if (!stepData || !stepData.enabled) {
            this.hide();
            return this;
        }
        
        // If there's no QR code content, just show the element (if it exists)
        if (!stepData.qrContent || !stepData.dataUrl) {
            this.show();
            return this;
        }
        
        // Update or create the QR code element
        this.updateOrCreateQRCode(stepData);
        
        return this;
    }
    
    /**
     * Handle side change
     */
    handleSideChange(isCurrentSide) {
        if (isCurrentSide) {
            // This handler's side is now active
            this.processTemplate();
        } else {
            // This handler's side is now inactive - hide QR code
            this.hide();
        }
        
        return this;
    }
    
    /**
     * Update or create the QR code element on the canvas
     * 
     * @param {object} stepData - Step data with QR code information
     */
    updateOrCreateQRCode(stepData) {
        // If we don't have a QR code data URL, we can't display anything
        if (!stepData.dataUrl) return;
        
        // If we already have a QR code element, update it
        if (this.qrCodeElement) {
            // Update the existing element with new QR code image
            this.qrCodeElement.setSrc(stepData.dataUrl).then(() => {
                this.qrCodeElement.set('visible', true);
                this.isVisible = true;
                this.canvas.canvas.requestRenderAll();
            });
            return;
        }
        
        // We don't have an element, so create a new one
        this.createQRCodeElement(stepData);
    }
    
    /**
     * Create a new QR code element on the canvas
     * 
     * @param {object} stepData - Step data with QR code information
     */
    createQRCodeElement(stepData) {
        // Calculate position for the new QR code (center of canvas)
        const canvasWidth = this.canvas.canvas.width;
        const canvasHeight = this.canvas.canvas.height;
        
        // Create new image element for QR code
        Image.fromURL(stepData.dataUrl).then((img) => {
            // Set up image properties
            img.set({
                name: this.qrCodeElementId,
                originX: 'center',
                originY: 'center',
                left: stepData.left || canvasWidth / 2,
                top: stepData.top || canvasHeight / 2,
                scaleX: stepData.scaleX || 0.5,
                scaleY: stepData.scaleY || 0.5,
                selectable: this.isActive,
                hasControls: this.isActive && (this.definition.allowedActions?.includes('scale') ?? true),
                lockMovementX: !(this.isActive && (this.definition.allowedActions?.includes('move') ?? false)),
                lockMovementY: !(this.isActive && (this.definition.allowedActions?.includes('move') ?? false)),
                cornerStyle: 'circle',
                transparentCorners: false,
                cornerColor: '#0d6efd',
                cornerSize: 50,
                borderScaleFactor:10,
                padding: 10,
                visible: true
            });
            
            // Add to canvas
            this.canvas.canvas.add(img);
            
            // Store reference
            this.qrCodeElement = img;
            this.elements.set(this.qrCodeElementId, img);
            this.canvas.elementRefs.set(this.qrCodeElementId, img);
            
            // Add listener for position changes
            img.on('modified', () => {
                const stepData = this.getStepData() || {};
                if (stepData) {
                    stepData.left = img.left;
                    stepData.top = img.top;
                    stepData.scaleX = img.scaleX;
                    stepData.scaleY = img.scaleY;
                    this.setStepData(stepData, false); // Update without recursive update
                }
            });
            
            // Update visibility and render
            this.isVisible = true;
            this.canvas.canvas.requestRenderAll();
        });
    }
    
    /**
     * Handle data received from the QR code modal
     * 
     * @param {object} data - QR code data from modal
     */
    handleQRCodeData(data) {
        // Get current step data or create new
        const stepData = this.getStepData() || {};
        
        // Set the step data to store it
        this.setStepData({
            ...stepData,
            ...data,
            enabled: true
        });
        
        // Update the panel to show the new data
        this.updatePanel();
    }
    
    /**
     * Render panel content
     */
    renderPanelContent() {
        const stepData = this.getStepData() || {};
        const hasData = stepData.qrContent;
        const price = this.definition.price || 0;
        
        return `
            <div class="stc-qrcode-field">
                <div class="stc-qrcode-header">
                    <label class="stc-switch">
                        <input type="checkbox" 
                               class="stc-qrcode-toggle"
                               ${stepData?.enabled ? 'checked' : ''} 
                               data-price="${price}">
                        <span class="stc-slider"></span>
                    </label>
                    <span class="stc-toggle-label">
                        Enable QR Code
                        ${price ? `<span class="stc-price">(+$${price})</span>` : ''}
                    </span>
                </div>
                
                <div class="stc-qrcode-summary ${stepData?.enabled ? '' : 'stc-qrcode-disabled'}">
                    ${hasData ? 
                        `<div class="stc-qrcode-info">
                            <div class="stc-qrcode-preview">
                                <div class="stc-qrcode-image-preview">
                                    ${stepData.dataUrl ? 
                                        `<img src="${stepData.dataUrl}" alt="QR Code">` : 
                                        `<div class="stc-qrcode-placeholder">QR Code Preview</div>`}
                                </div>
                            </div>
                            <div class="stc-qrcode-content">
                                ${stepData.qrContent ? `<div class="stc-qrcode-text">${stepData.qrContent}</div>` : ''}
                            </div>
                        </div>` : 
                        '<div class="stc-qrcode-placeholder">No QR code information added</div>'}
                </div>
                
                <button type="button" class="stc-qrcode-configure-btn" ${!stepData?.enabled ? 'disabled' : ''}>
                    ${hasData ? 'Edit QR Code' : 'Create QR Code'}
                </button>
                
                <div class="stc-step-controls">
                    <button type="button" class="stc-clear-step" ${!hasData || !stepData?.enabled ? 'disabled' : ''}>
                        Clear
                    </button>
                </div>
            </div>
        `;
    }
    
    /**
     * Bind panel events
     */
    bindPanelEvents() {
        if (!this.panelElement) return;
        
        const toggleSwitch = this.panelElement.querySelector('.stc-qrcode-toggle');
        const configureButton = this.panelElement.querySelector('.stc-qrcode-configure-btn');
        const clearButton = this.panelElement.querySelector('.stc-clear-step');
        const qrcodeSummary = this.panelElement.querySelector('.stc-qrcode-summary');
        
        // Bind toggle switch
        if (toggleSwitch) {
            this.addEventListener(toggleSwitch, 'change', () => {
                const stepData = this.getStepData() || {};
                stepData.enabled = toggleSwitch.checked;
                
                // Update UI elements
                if (qrcodeSummary) {
                    qrcodeSummary.classList.toggle('stc-qrcode-disabled', !stepData.enabled);
                }
                
                if (configureButton) {
                    configureButton.disabled = !stepData.enabled;
                }
                
                if (clearButton) {
                    clearButton.disabled = !stepData.enabled || !stepData.qrContent;
                }
                
                this.setStepData(stepData);
            });
        }
        
        // Bind configure button
        if (configureButton) {
            this.addEventListener(configureButton, 'click', () => {
                this.openQRCodeModal();
            });
        }
        
        // Bind clear button
        if (clearButton) {
            this.addEventListener(clearButton, 'click', () => {
                this.clear();
            });
        }
    }
    
    /**
     * Clear QR code data and elements
     */
    onReset() {
        // If we have a QR code element, remove it
        if (this.qrCodeElement) {
            // Remove from canvas
            this.canvas.canvas.remove(this.qrCodeElement);
            
            // Remove references
            this.canvas.elementRefs.delete(this.qrCodeElementId);
            this.elements.delete(this.qrCodeElementId);
            
            // Clear element reference
            this.qrCodeElement = null;
        }
    }
    
    /**
     * Initialize and open QR code modal
     */
    openQRCodeModal() {
        const stepData = this.getStepData() || {};
        
        if (!this.qrCodeModal) {
            this.qrCodeModal = new QRCodeModal();
            
            // Add save handler
            this.qrCodeModal.on('save', ({ detail }) => {
                this.handleQRCodeData(detail);
            });
        }
        
        // Pass current data to modal
        this.qrCodeModal.setInitialData(stepData);
        this.qrCodeModal.show();
    }
    
    /**
     * Show QR code elements
     */
    show() {
        if (this.qrCodeElement) {
            this.qrCodeElement.set('visible', true);
            this.isVisible = true;
            this.canvas.canvas.requestRenderAll();
        }
        
        return this;
    }
    
    /**
     * Hide QR code elements
     */
    hide() {
        if (this.qrCodeElement) {
            this.qrCodeElement.set('visible', false);
            this.isVisible = false;
            this.canvas.canvas.requestRenderAll();
        }
        
        return this;
    }
    
    /**
     * Get export data for QR code step
     * 
     * @returns {object} Export data
     */
    getStepExportData() {
        // Get base export data
        const baseData = super.getStepExportData();
        const stepData = this.getStepData() || {};
        
        // Enhanced export data with save operation context
        return {
            ...baseData,
            qrRef: stepData.qrReferenceId,
            configRef: stepData.configReferenceId,
            hasGeneratedQRCode: !!(stepData.dataUrl && stepData.qrContent),
            qrCodeMetadata: {
                content: stepData.qrContent,
                type: stepData.qrType || 'url',
                size: stepData.qrSize || 'medium',
                color: stepData.qrColor || '#000000'
            },
            positioning: {
                left: stepData.left,
                top: stepData.top,
                scaleX: stepData.scaleX,
                scaleY: stepData.scaleY
            },
            qrCodeContent: {
                content: stepData.qrContent || '',
                type: stepData.qrType || 'url',
                size: stepData.qrSize || 'medium',
                color: stepData.qrColor || '#000000',
                
                // Element positioning
                position: this.qrCodeElement ? {
                    left: this.qrCodeElement.left,
                    top: this.qrCodeElement.top,
                    scaleX: this.qrCodeElement.scaleX,
                    scaleY: this.qrCodeElement.scaleY
                } : null,
                
                // Image data for the generated QR code
                imageSource: stepData.dataUrl ? {
                    type: 'generatedQR',
                    url: stepData.dataUrl,
                    needsProcessing: true, // Changed to true since we want to upload it
                    metadata: {
                        fileName: `qrcode_${this.stepId}.png`,
                        fileType: 'image/png',
                        generateOnServer: false, // We already generated it, just need to store it
                        qrContent: stepData.qrContent || ''
                    }
                } : null
            }
        };
    }
    
    /**
     * Clean up resources
     */
    dispose() {
        // Clean up QR code element if it exists
        if (this.qrCodeElement) {
            this.canvas.canvas.remove(this.qrCodeElement);
            this.canvas.elementRefs.delete(this.qrCodeElementId);
        }
        
        // Dispose of the QR code modal
        if (this.qrCodeModal) {
            // Remove event listeners
            this.qrCodeModal.off('save');
        }
        
        // Call parent dispose
        super.dispose();
    }

    /**
     * Check if QR code step has content that needs to be saved
     * 
     * @param {Object} stepData - Current step data
     * @returns {boolean} Whether step has saveable content
     */
    hasSaveableContent(stepData) {
        // Only save if:
        // 1. Step is enabled
        // 2. We have QR code content
        // 3. We have a generated QR code data URL
        return stepData && 
            stepData.enabled === true && 
            stepData.qrContent && 
            stepData.dataUrl;
    }

    /**
     * Create save operations for QR code content
     * 
     * @param {Object} stepData - Current step data
     * @returns {Promise<Array>} Array of save operation objects
     */
    async createSaveOperations(stepData) {
        const operations = [];
        
        if (!this.hasSaveableContent(stepData)) {
            return operations;
        }
        
        // Generate a unique reference ID for this QR code
        const qrReferenceId = `qrcode_${this.side}_${this.stepId}_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
        
        // Create the QR code image upload operation
        operations.push({
            type: 'image_upload',
            referenceId: qrReferenceId,
            dataUrl: stepData.dataUrl,
            imageType: 'qr_code',
            metadata: {
                stepId: this.stepId,
                side: this.side,
                fileName: `qrcode_${this.stepId}_${this.side}.png`,
                fileType: 'image/png',
                isQRCode: true,
                
                // QR code specific metadata
                qrCodeData: {
                    content: stepData.qrContent,
                    type: stepData.qrType || 'url',
                    size: stepData.qrSize || 'medium',
                    color: stepData.qrColor || '#000000',
                    backgroundColor: stepData.qrBackgroundColor || '#ffffff',
                    errorCorrectionLevel: stepData.qrErrorCorrection || 'M'
                },
                
                // Position and scaling information
                positioning: {
                    left: stepData.left,
                    top: stepData.top,
                    scaleX: stepData.scaleX,
                    scaleY: stepData.scaleY
                },
                
                // Processing instructions for regeneration if needed
                processingInstructions: {
                    canRegenerateOnServer: true,
                    regenerationData: {
                        content: stepData.qrContent,
                        type: stepData.qrType || 'url',
                        size: stepData.qrSize || 'medium',
                        color: stepData.qrColor || '#000000',
                        backgroundColor: stepData.qrBackgroundColor || '#ffffff'
                    }
                }
            }
        });
        
        // Also create a configuration save operation for the QR code parameters
        // This is useful for order management and potential regeneration
        const configReferenceId = `qrcode_config_${this.side}_${this.stepId}_${Date.now()}`;
        
        operations.push({
            type: 'config_save',
            referenceId: configReferenceId,
            configType: 'qr_code_config',
            metadata: {
                stepId: this.stepId,
                side: this.side,
                fileName: `qrcode_config_${this.stepId}_${this.side}.json`,
                fileType: 'application/json',
                isQRCodeConfig: true,
                
                // Complete QR code configuration
                qrCodeConfiguration: {
                    content: stepData.qrContent,
                    type: stepData.qrType || 'url',
                    displaySize: stepData.qrSize || 'medium',
                    visualSettings: {
                        foregroundColor: stepData.qrColor || '#000000',
                        backgroundColor: stepData.qrBackgroundColor || '#ffffff',
                        errorCorrectionLevel: stepData.qrErrorCorrection || 'M'
                    },
                    positioning: {
                        left: stepData.left,
                        top: stepData.top,
                        scaleX: stepData.scaleX,
                        scaleY: stepData.scaleY
                    },
                    imageReference: qrReferenceId, // Link to the image operation
                    
                    // Additional metadata for order processing
                    orderProcessingData: {
                        canBeRegenerated: true,
                        requiresQualityCheck: true,
                        printOptimized: false // QR codes need special print handling
                    }
                }
            }
        });
        
        // Update step data with reference IDs
        const updatedStepData = { ...stepData };
        updatedStepData.qrReferenceId = qrReferenceId;
        updatedStepData.configReferenceId = configReferenceId;
        this.setStepData(updatedStepData, false);
        
        return operations;
    }

    /**
     * Get priority for QR code save operations
     * QR codes are functional elements, so medium-high priority
     * 
     * @returns {number} Priority level (0-10)
     */
    getSaveOperationsPriority() {
        return 6; // Medium-high priority - functional elements
    }

    /**
     * Validate that QR code save operations completed successfully
     * 
     * @param {Array} completedOperations - Operations that were processed
     * @returns {boolean} Whether this step's requirements were met
     */
    validateSaveOperations(completedOperations) {
        const stepData = this.getStepData();
        
        // If no QR code content, validation passes
        if (!this.hasSaveableContent(stepData)) {
            return true;
        }
        
        // Look for our QR code image operation
        const imageOperation = completedOperations.find(op => 
            op.sourceStepId === this.stepId && 
            op.sourceSide === this.side &&
            op.type === 'image_upload' &&
            op.referenceId === stepData.qrReferenceId
        );
        
        if (!imageOperation) {
            console.error(`QR code image save operation not found for ${this.stepId}`);
            return false;
        }
        
        // Check if the image operation has a result with a URL
        if (!imageOperation.result || !imageOperation.result.url) {
            console.error(`QR code image save operation failed for ${this.stepId}:`, imageOperation.error);
            return false;
        }
        
        // Look for our configuration operation
        const configOperation = completedOperations.find(op => 
            op.sourceStepId === this.stepId && 
            op.sourceSide === this.side &&
            op.type === 'config_save' &&
            op.referenceId === stepData.configReferenceId
        );
        
        if (!configOperation) {
            console.error(`QR code configuration save operation not found for ${this.stepId}`);
            return false;
        }
        
        // Check if the configuration operation was processed
        if (!configOperation.processed) {
            console.error(`QR code configuration save operation failed for ${this.stepId}:`, configOperation.error);
            return false;
        }
        
        // Validate QR code content integrity
        const qrConfig = configOperation.metadata?.qrCodeConfiguration;
        if (!qrConfig || !qrConfig.content) {
            console.error(`Invalid QR code configuration data for ${this.stepId}`);
            return false;
        }
        
        // Additional validation: ensure QR content matches what we expect
        if (qrConfig.content !== stepData.qrContent) {
            console.error(`QR code content mismatch for ${this.stepId}. Expected: ${stepData.qrContent}, Got: ${qrConfig.content}`);
            return false;
        }
        
        console.log(`QR code validation passed for ${this.stepId}:`, {
            imageUrl: imageOperation.result.url,
            content: qrConfig.content,
            type: qrConfig.type
        });
        
        return true;
    }

    /**
     * Get image reference for export (new method for QR code images)
     * 
     * @returns {object|null} Image reference data or null if no QR code
     */
    getImageReferenceForExport() {
        const stepData = this.getStepData();
        
        // If no QR code data, return null
        if (!this.hasSaveableContent(stepData)) {
            return null;
        }
        
        // Create the reference object
        const reference = {
            referenceId: stepData.qrReferenceId || `qrcode_${this.stepId}_${Date.now()}`,
            stepId: this.stepId,
            side: this.side,
            type: 'qr_code',
            needsProcessing: true, // QR codes need to be uploaded
            metadata: {
                fileName: `qrcode_${this.stepId}_${this.side}.png`,
                fileType: 'image/png',
                isQRCode: true,
                qrContent: stepData.qrContent,
                qrType: stepData.qrType || 'url'
            }
        };
        
        // Include the data URL for processing
        reference.dataUrl = stepData.dataUrl;
        
        return reference;
    }

}